import { DOMAIN } from "../consts";
import getMiniappInitData from "../telegramMiniapp";
import { Sentry } from "../bugsink";

const telegramEnv = () => {
  const webApp = window.Telegram?.WebApp;
  return {
    has_telegram_object: !!window.Telegram,
    has_webapp: !!webApp,
    webapp_version: webApp?.version ?? null,
    webapp_platform: webApp?.platform ?? null,
    has_download_file: typeof webApp?.downloadFile === "function",
    has_open_link: typeof webApp?.openLink === "function",
    has_init_data: !!getMiniappInitData().tgWebAppData,
    user_agent: navigator.userAgent,
  };
};

const reportDownloadStep = (message, extras, level = "warning") => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    Object.entries(extras).forEach(([key, value]) => scope.setExtra(key, value));
    Sentry.captureMessage(message);
  });
};

const anchorDownload = (filename, blob) => {
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(url);
};

// In the mobile Telegram webview an anchor click can't save a blob, so the
// file is handed to the system share sheet instead (user saves it from there).
const saveFile = async (filename, blob) => {
  const platform = window.Telegram?.WebApp?.platform || "";
  const inTelegram = getMiniappInitData().tgWebAppData;
  if (
    inTelegram &&
    (platform === "android" || platform === "ios") &&
    typeof navigator.canShare === "function"
  ) {
    try {
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") {
        return;
      }
      reportDownloadStep("saveFile: navigator.share failed", {
        ...telegramEnv(),
        filename,
        error: String(e),
      }, "error");
    }
  }

  anchorDownload(filename, blob);
};

export const downloadConfig = async (
  token,
  subscriptionId,
  setIsLoading,
  hostname = "",
  subscriptionLogin = ""
) => {
  try {
    setIsLoading(true);

    const params = new URLSearchParams();
    if (hostname) {
      params.set("server_name", hostname);
    }
    const query = params.toString();
    const url =
      DOMAIN +
      "/subscriptions/" +
      subscriptionId +
      "/config" +
      (query ? "?" + query : "");

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let message = "Something went wrong";
      try {
        const errorBody = await response.json();
        if (typeof errorBody?.message === "string" && errorBody.message !== "") {
          message = errorBody.message;
        } else if (
          typeof errorBody?.detail === "string" &&
          errorBody.detail !== ""
        ) {
          message = errorBody.detail;
        }
      } catch {}
      reportDownloadStep("downloadConfig: config request failed", {
        ...telegramEnv(),
        url,
        subscription_id: subscriptionId,
        status: response.status,
        error_message: message,
      }, "error");
      return message;
    }

    const fallbackName = "gravizapp.ovpn";
    const filename = response.headers.get("config-filename") || fallbackName;

    // Native Telegram download dialog, no timers or fallbacks — the client
    // either shows it right away or the generic blob fallback below runs.
    const webApp = window.Telegram?.WebApp;
    if (
      getMiniappInitData().tgWebAppData &&
      typeof webApp?.downloadFile === "function"
    ) {
      try {
        webApp.downloadFile({ url, file_name: filename });
        return "";
      } catch (e) {
        reportDownloadStep(
          "downloadConfig: downloadFile is not supported by this Telegram client",
          {
            ...telegramEnv(),
            url,
            filename,
            error: String(e),
          },
          "error"
        );
      }
    }

    const blob = await response.blob();
    await saveFile(filename, blob);

    return "";
  } catch (e) {
    reportDownloadStep("downloadConfig: unexpected failure", {
      subscription_id: subscriptionId,
      hostname,
      error: String(e),
    }, "error");
    return "Something went wrong";
  } finally {
    setIsLoading(false);
  }
};

export const getShadowsocksQr = async (token, subscriptionId, hostname) => {
  const data = {
    token,
    subscription_id: subscriptionId,
    hostname,
    content_type: "qr",
  };

  const response = await fetch(DOMAIN + "/account/get_shadowsocks_data", {
    method: "POST",
    body: JSON.stringify(data),
  });

  const contentType = response.headers.get("content-type") || "";
  if (!response.ok || contentType.includes("application/json")) {
    return "Something went wrong";
  }

  const filename = response.headers.get("config-filename");
  const blob = await response.blob();
  await saveFile(filename, blob);

  return "";
};

export const getShadowsocksLink = async (token, subscriptionId, hostname) => {
  const data = {
    token,
    subscription_id: subscriptionId,
    hostname,
    content_type: "link",
  };

  const response = await fetch(DOMAIN + "/account/get_shadowsocks_data", {
    method: "POST",
    body: JSON.stringify(data),
  }).then((r) => r.json());

  return response;
};

export const downloadArchive = async (token, subscriptionId, setIsLoading) => {
  try {
    setIsLoading(true);

    const data = {
      token,
      subscription_id: subscriptionId,
    };

    const response = await fetch(DOMAIN + "/account/download_archive", {
      method: "POST",
      body: JSON.stringify(data),
    });

    const contentType = response.headers.get("content-type") || "";
    if (!response.ok || contentType.includes("application/json")) {
      return "Something went wrong";
    }

    const filename = response.headers.get("config-filename");
    const blob = await response.blob();
    await saveFile(filename, blob);

    return "";
  } finally {
    setIsLoading(false);
  }
};
